import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { Subject } from 'rxjs';
import { CalendarDateFormatter, CalendarEvent, CalendarView, CalendarCommonModule, CalendarMonthModule, CalendarWeekModule, CalendarDayModule } from 'angular-calendar';
import { MatDialog } from '@angular/material/dialog';
import { taskStatuses } from 'src/assets/data/task-statuses';
import { ScheduleTasksListModalComponent } from 'src/app/components/schedule/schedule-tasks-list-modal/schedule-tasks-list-modal.component';
import { ScheduleDetailComponent, TASK_DETAIL_DIALOG_SIZE } from 'src/app/components/schedule/schedule-detail/schedule-detail.component';
import { DatePipe, formatDate } from '@angular/common';
import { MatFormField } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { ItemTemplateComponent } from '../item-template/item-template.component';

/**
 * Month/week/day the coordinator was looking at, for the rest of the session. Leaving the calendar (to a client, an
 * assembly order…) and coming back used to start again on the current month.
 */
export const calendarSession: { view?: CalendarView; viewDate?: Date } = {};

/** Date range of a calendar view (same rules the calendar has always used to ask for its tasks). */
export function calendarRange(view: string, viewDate: Date): { from: Date; to: Date } {
  switch (view) {
    case 'day':
      return { from: startOfDay(viewDate), to: endOfDay(viewDate) };
    case 'week':
      return { from: startOfWeek(viewDate), to: endOfWeek(viewDate) };
    default:
      return { from: startOfMonth(viewDate), to: endOfMonth(viewDate) };
  }
}

const SUNDAY_HIDDEN = [0];
const ALL_DAYS: number[] = [];

/** Tablet y pantallas más chicas: mismo corte que usa la navegación compacta de la app. */
export const COMPACT_CALENDAR_QUERY = '(max-width: 1023.98px)';

/** Primer domingo del período en pantalla que tiene alguna tarea, o null si no hay ninguno. */
export function firstSundayWithTask(view: string, viewDate: Date, events: { start: Date }[]): Date | null {
  const { from, to } = calendarRange(view, viewDate);
  const sundays = events
    .map((e) => new Date(e.start))
    .filter((start) => start >= from && start <= to && start.getDay() === 0)
    .sort((a, b) => a.getTime() - b.getTime());
  return sundays.length ? startOfDay(sundays[0]) : null;
}

/**
 * Days the week and month views leave out. Sunday is rarely worked, so it is hidden to give the other days room, but
 * only while no task on screen falls on a Sunday: a task is never hidden.
 * En tablet y pantallas chicas (`compact`) el domingo se oculta siempre; sus tareas siguen en la vista diaria.
 */
export function excludedDays(view: string, viewDate: Date, events: { start: Date }[], compact = false): number[] {
  if (compact) {
    return SUNDAY_HIDDEN;
  }
  return firstSundayWithTask(view, viewDate, events) ? ALL_DAYS : SUNDAY_HIDDEN;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Toolbar title in Spanish order ("Lunes 19 de octubre de 2026", "18 – 24 de octubre de 2026"). angular-calendar's
 * default formatter uses US patterns ("EEEE, MMMM d, y"), which read "Lunes, Octubre 19, 2026" in Spanish.
 */
export function calendarTitle(view: string, viewDate: Date): string {
  const format = (date: Date, pattern: string) => formatDate(date, pattern, 'es');
  switch (view) {
    case 'day':
      return capitalize(format(viewDate, "EEEE d 'de' MMMM 'de' y"));
    case 'week': {
      const { from, to } = calendarRange(view, viewDate);
      const end = format(to, "d 'de' MMMM 'de' y");
      if (from.getFullYear() !== to.getFullYear()) {
        return `${format(from, "d 'de' MMMM 'de' y")} – ${end}`;
      }
      if (from.getMonth() !== to.getMonth()) {
        return `${format(from, "d 'de' MMMM")} – ${end}`;
      }
      return `${format(from, 'd')} – ${end}`;
    }
    default:
      return capitalize(format(viewDate, 'LLLL y'));
  }
}

import { SpanishCalendarDateFormatter } from '../spanish-date-formatter';
@Component({
    selector: 'ind-calendar',
    providers: [{ provide: CalendarDateFormatter, useClass: SpanishCalendarDateFormatter }],
    templateUrl: './ind-calendar.component.html',
    styleUrls: ['./ind-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CalendarCommonModule, MatFormField, MatSelect, MatOption, ItemTemplateComponent, CalendarMonthModule, CalendarWeekModule, CalendarDayModule, DatePipe]
})
export class IndCalendarComponent {
  dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  /** True en tablet y pantallas chicas, donde las vistas semana y mes nunca muestran el domingo. */
  compact = false;

  constructor() {
    inject(BreakpointObserver)
      .observe(COMPACT_CALENDAR_QUERY)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.compact = state.matches;
        this.cdr.markForCheck(); // OnPush
      });
  }

  @Output() closeOpenMonthViewDayEvent = new EventEmitter<any>();
  @Output() closeModalEvent = new EventEmitter<any>();
  @Output() closelistModalEvent = new EventEmitter<any>();
  @Output() showNotScheduledTaskEvent = new EventEmitter<boolean>();
  @Input() events: any[] = [];
  @Input() showNotScheduledTask: boolean = true;
  /** Last refresh time, shown as muted text in the toolbar (the page has no separate header). */
  @Input() lastUpdate?: Date;

  //Custom modal component
  @Input() modalComponent: any;

  view: CalendarView = calendarSession.view ?? CalendarView.Month;
  CalendarView = CalendarView;
  viewDate: Date = calendarSession.viewDate ?? new Date();

  refresh = new Subject<void>();

  activeDayIsOpen: boolean = false;
  taskStatuses: any[] = taskStatuses;

  /** Toolbar title for the period on screen. */
  title(): string {
    return calendarTitle(this.view, this.viewDate);
  }

  emitShowNotScheduledTask() {
    this.showNotScheduledTaskEvent.emit(true);
    this.showNotScheduledTask = true;
  }

  openListDialogAction(calendarEvent: any): void {
    if (calendarEvent.day.events.length == 0) {
      return;
    }

    let CurrentDialog = this.dialog.open(ScheduleTasksListModalComponent, {
      minWidth: 'min(60vw, 960px)',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '95vh',
      panelClass: '.detailClass',
      data: { event: calendarEvent.day },
    });

    CurrentDialog.afterClosed().subscribe((result) => {
      this.closelistModalEvent.emit({ event: calendarEvent.day.events });
    });
  }

  dialogAction(action: string, calendarEvent: any): void {
    let CurrentDialog = this.dialog.open(ScheduleDetailComponent, {
      ...TASK_DETAIL_DIALOG_SIZE,
      panelClass: '.detailClass',
      data: { event: calendarEvent.event, action: action },
    });

    CurrentDialog.afterClosed().subscribe((result) => {
      this.closeModalEvent.emit({ event: calendarEvent.event, action: action });
    });
  }

  addEvent(newEvent: any): void {
    this.events = [...this.events, newEvent];
  }

  setEvents(events: any[]): void {
    this.events = events.slice().sort((a, b) => {
      return (
        new Date(a.event.data.schudeledDate).getTime() -
        new Date(b.event.data.schudeledDate).getTime()
      );
    });
  }

  deleteEvent(eventToDelete: CalendarEvent) {
    this.events = this.events.filter((event) => event !== eventToDelete);
  }

  setView(view: CalendarView) {
    this.emitChange(view, this.viewDate);
    this.activeDayIsOpen = false;
    this.view = view;
  }

  closeOpenMonthViewDay(viewDate: Date, view: string) {
    this.emitChange(view, viewDate);
    this.activeDayIsOpen = false;
  }

  emitChange(view: string, viewDate: Date) {
    calendarSession.view = view as CalendarView;
    calendarSession.viewDate = viewDate;
    this.closeOpenMonthViewDayEvent.emit(calendarRange(view, viewDate));
  }

  /** Days left out of the week and month views (see excludedDays). */
  excludeDays(): number[] {
    return excludedDays(this.view, this.viewDate, this.events, this.compact);
  }

  /**
   * Domingo con tareas que la vista semana o mes no muestra (solo pasa en tablet y pantallas chicas), para avisarlo y
   * que no se pierda la tarea.
   */
  hiddenSunday(): Date | null {
    if (!this.compact || this.view === CalendarView.Day) {
      return null;
    }
    return firstSundayWithTask(this.view, this.viewDate, this.events);
  }

  /** Abre en la vista diaria el domingo que la vista semana o mes deja afuera. */
  showSunday(sunday: Date): void {
    this.viewDate = sunday;
    this.setView(CalendarView.Day);
  }

  /** True when today falls inside the period on screen, so "Hoy" has nothing to do. */
  showsToday(): boolean {
    const { from, to } = calendarRange(this.view, this.viewDate);
    const now = new Date();
    return now >= from && now <= to;
  }

  getStatusColor(status: any) {
    return this.taskStatuses.find((x) => x.value == status).eventColor
      .secondary;
  }

  getStatusSelectedColor(status: any) {
    return this.taskStatuses.find((x) => x.value == status).eventColor.primary;
  }

  getStatusDescription(status: any) {
    return this.taskStatuses.find((x) => x.value == status).name;
  }
}
